import { Injectable, inject, signal, computed, effect } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom, catchError } from 'rxjs';
import { SystemService } from '@shared/service/system/system.service';
import { EventService } from '@shared/service/event.service';
import { AuthService } from '../../../auth/service/auth.service';
import { mapSnakeToCamel } from '@shared/utils/type-transformers';
import {
  ProcessStatus,
  ProjectUser,
  Workflow
} from './dashboard-data-cache.interface';
import {
  DASHBOARD_CACHE_CONFIG,
  DASHBOARD_CACHE_ENDPOINTS
} from './dashboard-data-cache.config';

// Re-export interfaces for convenience
export type { ProcessStatus, ProjectUser, Workflow } from './dashboard-data-cache.interface';

@Injectable({
  providedIn: 'root'
})
export class DashboardDataCacheService {
  private http = inject(HttpClient);
  private systemService = inject(SystemService);
  private eventService = inject(EventService);
  private authService = inject(AuthService);

  // ===== STATIC DATA SIGNALS =====

  // Process Statuses (Static - fetched once per app lifecycle)
  private _processStatuses = signal<ProcessStatus[]>([]);
  private _processStatusesLoading = signal(false);
  private _processStatusesError = signal<string | null>(null);
  private _processStatusesLastFetched = signal<number | null>(null);

  // Public readonly signals
  readonly processStatuses = this._processStatuses.asReadonly();
  readonly isProcessStatusesLoading = this._processStatusesLoading.asReadonly();
  readonly processStatusesError = this._processStatusesError.asReadonly();

  // ===== PROJECT-SCOPED DATA SIGNALS =====

  // Project Users (Project-scoped - refetched on project change)
  private _projectUsers = signal<ProjectUser[]>([]);
  private _projectUsersLoading = signal(false);
  private _projectUsersError = signal<string | null>(null);
  private _projectUsersLastFetched = signal<number | null>(null);
  private _cachedProjectName = signal<string | null>(null);

  // Public readonly signals
  readonly projectUsers = this._projectUsers.asReadonly();
  readonly isProjectUsersLoading = this._projectUsersLoading.asReadonly();
  readonly projectUsersError = this._projectUsersError.asReadonly();

  // ===== PROJECT-SCOPED WORKFLOWS =====

  // Workflows (Project-scoped - refetched on project change)
  private _workflows = signal<Workflow[]>([]);
  private _workflowsLoading = signal(false);
  private _workflowsError = signal<string | null>(null);
  private _workflowsLastFetched = signal<number | null>(null);

  // Public readonly signals
  readonly workflows = this._workflows.asReadonly();
  readonly isWorkflowsLoading = this._workflowsLoading.asReadonly();
  readonly workflowsError = this._workflowsError.asReadonly();

  // Computed: Are any resources loading?
  readonly isAnyLoading = computed(() =>
    this._processStatusesLoading() || this._projectUsersLoading() || this._workflowsLoading()
  );

  // Computed: Project users formatted for dropdowns
  readonly projectUsersOptions = computed(() =>
    this._projectUsers().map(user => ({
      label: `${user.firstName} ${user.lastName}`.trim() || user.username,
      value: user.id.toString(),
      data: user
    }))
  );

  constructor() {
    // Effect: Watch for project changes and refetch project-scoped data
    effect(() => {
      const currentProject = this.systemService.getSelectedProject();
      const cachedProject = this._cachedProjectName();

      // Skip if project hasn't changed
      if (currentProject === cachedProject) {
        return;
      }

      // Skip if no project selected
      if (!currentProject) {
        this.clearProjectScopedData();
        return;
      }

      console.log('[DashboardDataCache] Project changed:', {
        from: cachedProject,
        to: currentProject
      });

      // Update cached project name BEFORE fetching to prevent infinite loop
      // This ensures the effect won't re-run even if fetches fail
      this._cachedProjectName.set(currentProject);

      // Fetch project-scoped data for new project
      // Only fetch project users if user has permission
      const permissions = this.authService.getCurrentProjectUserPermissions();
      // @ts-ignore
      if (permissions?.hasProjectSessionShow) {
        this.fetchProjectUsers(currentProject);
      }
      this.fetchWorkflows(currentProject);
    });
  }

  /**
   * Initialize cache - fetch static data
   * Call this from app initialization (after SystemService.initialize())
   */
  async initialize(): Promise<void> {
    const projectName = this.systemService.getSelectedProject();

    if (!projectName) {
      console.warn('[DashboardDataCache] Cannot initialize - no project selected');
      return;
    }

    console.log('[DashboardDataCache] Initializing cache...');

    // Fetch static data (only once)
    await this.fetchProcessStatuses(projectName);

    console.log('[DashboardDataCache] Cache initialized successfully');
  }

  /**
   * Manually refresh all cached data
   */
  async refreshAll(): Promise<void> {
    const projectName = this.systemService.getSelectedProject();

    if (!projectName) {
      console.warn('[DashboardDataCache] Cannot refresh - no project selected');
      return;
    }

    const permissions = this.authService.getCurrentProjectUserPermissions();
    const fetchTasks = [
      this.fetchProcessStatuses(projectName, true),
      this.fetchWorkflows(projectName, true)
    ];

    // Only fetch project users if user has permission
    if (permissions?.permissions?.hasProjectSessionShow) {
      fetchTasks.push(this.fetchProjectUsers(projectName, true));
    }

    await Promise.all(fetchTasks);
  }

  /**
   * Manually refresh process statuses
   */
  async refreshProcessStatuses(): Promise<void> {
    const projectName = this.systemService.getSelectedProject();

    if (!projectName) return;

    await this.fetchProcessStatuses(projectName, true);
  }

  /**
   * Manually refresh project users
   */
  async refreshProjectUsers(): Promise<void> {
    const projectName = this.systemService.getSelectedProject();

    if (!projectName) return;

    const permissions = this.authService.getCurrentProjectUserPermissions();
    if (!permissions?.permissions?.hasProjectSessionShow) {
      console.warn('[DashboardDataCache] User lacks permission to fetch project users');
      return;
    }

    await this.fetchProjectUsers(projectName, true);
  }

  /**
   * Manually refresh workflows
   */
  async refreshWorkflows(): Promise<void> {
    const projectName = this.systemService.getSelectedProject();

    if (!projectName) return;

    await this.fetchWorkflows(projectName, true);
  }

  // ===== PRIVATE FETCH METHODS =====

  /**
   * Fetch process statuses from API
   * @param projectName - Project identifier
   * @param forceRefresh - Bypass cache and fetch fresh data
   */
  private async fetchProcessStatuses(
    projectName: string,
    forceRefresh = false
  ): Promise<void> {
    // Check if already cached and not forcing refresh
    if (!forceRefresh && this._processStatuses().length > 0) {
      console.log('[DashboardDataCache] Process statuses already cached');
      return;
    }

    // Check if already loading
    if (this._processStatusesLoading()) {
      console.log('[DashboardDataCache] Process statuses already loading');
      return;
    }

    this._processStatusesLoading.set(true);
    this._processStatusesError.set(null);

    try {
      const url = DASHBOARD_CACHE_ENDPOINTS.PROCESS_STATUSES(projectName);

      const response = await firstValueFrom(
        this.http.get<{
          data: { statuses: { id: number; text: string }[] };
          meta: unknown
        }>(url).pipe(
          catchError(error => {
            console.error('[DashboardDataCache] Process statuses fetch failed:', error);
            throw error;
          })
        )
      );

      // Extract and transform data
      const statuses = response?.data?.statuses || [];
      const processStatuses: ProcessStatus[] = statuses.map(item => ({
        id: item.id,
        text: item.text
      }));

      // Update signals
      this._processStatuses.set(processStatuses);
      this._processStatusesLastFetched.set(Date.now());

      console.log('[DashboardDataCache] Process statuses cached:', {
        count: processStatuses.length,
        sample: processStatuses.slice(0, 3)
      });

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      this._processStatusesError.set(errorMessage);

      this.eventService.emit('toast-error', {
        header: 'Data Load Error',
        payload: 'Failed to load process statuses'
      });

    } finally {
      this._processStatusesLoading.set(false);
    }
  }

  /**
   * Fetch project users from API
   * @param projectName - Project identifier
   * @param forceRefresh - Bypass cache and fetch fresh data
   */
  private async fetchProjectUsers(
    projectName: string,
    forceRefresh = false
  ): Promise<void> {
    // Check TTL if not forcing refresh
    if (!forceRefresh) {
      const lastFetched = this._projectUsersLastFetched();
      const cachedProject = this._cachedProjectName();
      const ttl = DASHBOARD_CACHE_CONFIG.PROJECT_USERS.ttl;

      // If same project and within TTL, use cached data
      if (
        cachedProject === projectName &&
        lastFetched &&
        ttl &&
        Date.now() - lastFetched < ttl
      ) {
        console.log('[DashboardDataCache] Project users within TTL, using cache');
        return;
      }
    }

    // Check if already loading
    if (this._projectUsersLoading()) {
      console.log('[DashboardDataCache] Project users already loading');
      return;
    }

    this._projectUsersLoading.set(true);
    this._projectUsersError.set(null);

    try {
      const url = DASHBOARD_CACHE_ENDPOINTS.PROJECT_USERS(projectName);

      const params = new HttpParams()
        .set('in_company', 'true')
        .set('page_size', '1000');

      const response = await firstValueFrom(
        this.http.get<{ data: unknown[] }>(url, { params }).pipe(
          catchError(error => {
            console.error('[DashboardDataCache] Project users fetch failed:', error);
            throw error;
          })
        )
      );

      // Transform data
      const users = response.data.map((user: unknown) =>
        mapSnakeToCamel(user) as ProjectUser
      );

      // Update signals
      this._projectUsers.set(users);
      this._projectUsersLastFetched.set(Date.now());

      console.log('[DashboardDataCache] Project users cached:', {
        project: projectName,
        count: users.length,
        sample: users.slice(0, 3)
      });

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      this._projectUsersError.set(errorMessage);

      this.eventService.emit('toast-error', {
        header: 'Data Load Error',
        payload: 'Failed to load project users'
      });

    } finally {
      this._projectUsersLoading.set(false);
    }
  }

  /**
   * Fetch workflows from API
   * @param projectName - Project identifier
   * @param forceRefresh - Bypass cache and fetch fresh data
   */
  private async fetchWorkflows(
    projectName: string,
    forceRefresh = false
  ): Promise<void> {
    // Check TTL if not forcing refresh
    if (!forceRefresh) {
      const lastFetched = this._workflowsLastFetched();
      const cachedProject = this._cachedProjectName();
      const ttl = DASHBOARD_CACHE_CONFIG.WORKFLOWS.ttl;

      // If same project and within TTL, use cached data
      if (
        cachedProject === projectName &&
        lastFetched &&
        ttl &&
        Date.now() - lastFetched < ttl
      ) {
        console.log('[DashboardDataCache] Workflows within TTL, using cache');
        return;
      }
    }

    // Check if already loading
    if (this._workflowsLoading()) {
      console.log('[DashboardDataCache] Workflows already loading');
      return;
    }

    this._workflowsLoading.set(true);
    this._workflowsError.set(null);

    try {
      const url = DASHBOARD_CACHE_ENDPOINTS.WORKFLOWS(projectName);

      const params = new HttpParams()
        .set('is_autogenerated', 'false')
        .set('ordering', '-modified_at')
        .set('page_size', '100');

      const response = await firstValueFrom(
        this.http.get<{ data: { id: string; name: string; project?: string }[]; meta: unknown }>(url, { params }).pipe(
          catchError(error => {
            console.error('[DashboardDataCache] Workflows fetch failed:', error);
            throw error;
          })
        )
      );

      // Extract and transform data
      const workflowsData = response?.data || [];
      const workflows: Workflow[] = workflowsData.map(item => ({
        uuid: item.id,
        name: item.name,
        description: item.name // Use name as description fallback
      }));

      // Update signals
      this._workflows.set(workflows);
      this._workflowsLastFetched.set(Date.now());

      console.log('[DashboardDataCache] Workflows cached:', {
        project: projectName,
        count: workflows.length,
        sample: workflows.slice(0, 3)
      });

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      this._workflowsError.set(errorMessage);

      this.eventService.emit('toast-error', {
        header: 'Data Load Error',
        payload: 'Failed to load workflows'
      });

    } finally {
      this._workflowsLoading.set(false);
    }
  }

  /**
   * Clear project-scoped data when no project is selected
   */
  private clearProjectScopedData(): void {
    this._projectUsers.set([]);
    this._projectUsersLastFetched.set(null);
    this._workflows.set([]);
    this._workflowsLastFetched.set(null);
    this._cachedProjectName.set(null);
    this._projectUsersError.set(null);
    this._workflowsError.set(null);
  }

  /**
   * Get process status by name
   */
  getProcessStatusByName(statusName: string): ProcessStatus | undefined {
    return this._processStatuses().find(s => s.text === statusName);
  }

  /**
   * Get project user by ID
   */
  getProjectUserById(userId: number): ProjectUser | undefined {
    return this._projectUsers().find(u => u.id === userId);
  }

  /**
   * Get workflow by UUID
   */
  getWorkflowByUuid(uuid: string): Workflow | undefined {
    return this._workflows().find(w => w.uuid === uuid);
  }

  /**
   * Check if cache is stale (exceeds TTL)
   */
  isProjectUsersCacheStale(): boolean {
    const lastFetched = this._projectUsersLastFetched();
    const ttl = DASHBOARD_CACHE_CONFIG.PROJECT_USERS.ttl;

    if (!lastFetched || !ttl) return false;

    return Date.now() - lastFetched > ttl;
  }

  /**
   * Check if workflows cache is stale (exceeds TTL)
   */
  isWorkflowsCacheStale(): boolean {
    const lastFetched = this._workflowsLastFetched();
    const ttl = DASHBOARD_CACHE_CONFIG.WORKFLOWS.ttl;

    if (!lastFetched || !ttl) return false;

    return Date.now() - lastFetched > ttl;
  }
}
